import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";
import { Slot } from "radix-ui";

/**
 * Reusable Button Component
 *
 * @param {ReactNode} children - Content inside the button
 * @param {string} className - custom Tailwind / CSS classes
 * @param {string} type - Button type (default: "button")
 * @param {object} props - Other native button props (onClick, disabled, etc.)
 */
export const Button = ({
  children,
  className = "",
  type = "button", // Prevents accidental form submission
  ...props
}) => {
  return (
    <button type={type} className={cn("px-6 py-2 bg-transparent rounded-3xl", className)} {...props}>
      {children}
    </button>
  );
};

export const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // `bg-button-primary` is an app token — near-black in light, white in
        // dark — so its label has to follow the same switch. `text-tertiary`
        // is that token: white on the dark button, dark on the white one.
        // `text-primary-foreground` is shadcn's, and it is near-white in both
        // themes, which put a white label on the white dark-theme button.
        default: "bg-button-primary text-text-tertiary [a]:hover:bg-button-primary/80",
        // No base text color used to be set here — the label fell back to
        // the browser's default button text, which doesn't answer to the
        // app's theme toggle the way `text-text-primary` (the app's own
        // token, see themes/styles/*.js) does.
        outline: "border-border bg-background text-text-primary hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground ",
        // Same mismatch: `bg-button-secondary` is a saturated blue in both
        // themes, so the label is white in both. `text-secondary-foreground`
        // is near-black under the light theme.
        secondary: "bg-button-secondary text-text-base hover:bg-button-secondary/80 aria-expanded:bg-button-secondary aria-expanded:text-text-base",
        ghost: "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground ",
        // The neutral half of a Save/Cancel pair. `bg-button-tertiary` is the
        // app's own token for it — `JobForm` reaches for it by hand on every
        // Cancel and Back button — and it had no variant here, so anything
        // built from `ButtonShadcn` had to repeat the classes or settle for
        // `ghost`, which reads as "not a button" beside a filled primary.
        tertiary: "bg-button-tertiary text-text-primary hover:bg-button-tertiary/70 aria-expanded:bg-button-tertiary",
        destructive: "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 ",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        icon: "size-8",
        "icon-xs": "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

/**
 * `type="button"` by default, the way `Button` above already does.
 *
 * A bare `<button>` inside a `<form>` is `type="submit"`, and this component set
 * no default of its own, so any shared component built from it became a submit
 * button the moment somebody dropped it into a form — the scope control in the
 * note composer did exactly that, and picking a scope posted the half-written
 * note. Every form in the app already declares `type="submit"` where it wants
 * one, so the default costs nothing and closes the trap for the next component.
 *
 * `asChild` is left alone. There the props land on whatever child was passed —
 * often an anchor or a Radix trigger — and `type` means something different or
 * nothing at all on those, so it is forwarded only when the caller asked for it.
 */
export const ButtonShadcn = ({ className, variant = "default", size = "default", asChild = false, type, ...props }) => {
  const Comp = asChild ? Slot.Root : "button";
  const typeProp = asChild ? (type === undefined ? {} : { type }) : { type: type ?? "button" };

  return <Comp data-slot="button" data-variant={variant} data-size={size} {...typeProp} className={cn(buttonVariants({ variant, size, className }))} {...props} />;
};
